import { motion } from 'framer-motion';
import { Mail, RefreshCw, Gift, Wallet } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

// --- Workflow Step Helpers ---
export const getExchangeSteps = (config) => [
  { id: 'request', label: 'Richiesta', desc: 'Il cliente seleziona gli articoli per il cambio', fixed: true, active: true, email: { template: 'exchange_confirmation', label: 'Conferma richiesta cambio (recap ordine + link pagamento)' } },
  { id: 'initial_approval', label: 'Approvazione Iniziale', desc: 'Lo staff approva la richiesta prima di procedere', fixed: false, active: !!config.initial_approval, toggleKey: 'initial_approval' },
  { id: 'payment', label: 'Pagamento', desc: 'Il cliente paga la differenza (ordine bozza)', fixed: true, active: true },
  { id: 'shipping', label: 'Spedizione', desc: 'Il cliente spedisce il pacco con il reso', fixed: true, active: true },
  { id: 'arrival', label: 'Conferma Arrivo', desc: 'La logistica conferma la ricezione del pacco', fixed: true, active: true, email: !config.final_approval ? { template: 'exchange_in_progress', label: 'Cambio in lavorazione (pacco ricevuto, nuovo ordine in preparazione)' } : null },
  { id: 'final_approval', label: 'Approvazione Finale', desc: 'Lo staff approva dopo il controllo del pacco', fixed: false, active: !!config.final_approval, toggleKey: 'final_approval', email: config.final_approval ? { template: 'exchange_in_progress', label: 'Cambio approvato e in lavorazione' } : null },
  { id: 'processing', label: 'Lavorazione', desc: 'Il cambio viene preparato e spedito', fixed: true, active: true },
  { id: 'completed', label: 'Completato', desc: 'Ordine di cambio evaso', fixed: true, active: true },
];

export const getVoucherSteps = (config) => [
  { id: 'request', label: 'Richiesta', desc: 'Il cliente richiede il reso con voucher', fixed: true, active: true, email: { template: 'voucher_confirmation', label: 'Conferma richiesta voucher (recap articoli + valore voucher)' } },
  { id: 'initial_approval', label: 'Approvazione Iniziale', desc: 'Lo staff approva la richiesta prima che il cliente spedisca', fixed: false, active: !!config.initial_approval, toggleKey: 'initial_approval' },
  { id: 'shipping', label: 'Spedizione', desc: 'Il cliente spedisce il pacco', fixed: true, active: true },
  { id: 'arrival', label: 'Conferma Arrivo', desc: 'La logistica conferma la ricezione del pacco', fixed: true, active: true, email: !config.final_approval ? { template: 'voucher_approved', label: 'Voucher generato e inviato (con codice gift card)' } : null },
  { id: 'final_approval', label: 'Approvazione Voucher', desc: 'Lo staff approva e genera il voucher', fixed: false, active: !!config.final_approval, toggleKey: 'final_approval', email: config.final_approval ? { template: 'voucher_approved', label: 'Voucher approvato e generato (con codice gift card)' } : null },
  { id: 'completed', label: 'Completato', desc: 'Voucher generato e inviato al cliente', fixed: true, active: true },
];

export const getRMASteps = (config) => [
  { id: 'request', label: 'Richiesta', desc: 'Il cliente richiede il reso monetario', fixed: true, active: true, email: { template: 'rma_confirmation', label: 'Conferma richiesta reso (recap articoli + costo gestione + IBAN)' } },
  { id: 'initial_approval', label: 'Approvazione Iniziale', desc: 'Lo staff approva prima che il cliente spedisca', fixed: false, active: !!config.initial_approval, toggleKey: 'initial_approval' },
  { id: 'shipping', label: 'Spedizione', desc: 'Il cliente spedisce il pacco', fixed: true, active: true },
  { id: 'arrival', label: 'Conferma Arrivo', desc: 'La logistica conferma la ricezione del pacco', fixed: true, active: true, email: !config.final_approval ? { template: 'refund_approved', label: 'Rimborso approvato (importo e tempistiche di accredito)' } : null },
  { id: 'final_approval', label: 'Approvazione Rimborso', desc: 'Lo staff approva il rimborso dopo il controllo', fixed: false, active: !!config.final_approval, toggleKey: 'final_approval', email: config.final_approval ? { template: 'refund_approved', label: 'Rimborso approvato (importo e tempistiche di accredito)' } : null },
  { id: 'processing', label: 'Emissione Rimborso', desc: 'Il rimborso viene emesso su Shopify', fixed: true, active: true },
  { id: 'completed', label: 'Completato', desc: 'Rimborso completato', fixed: true, active: true },
];

// --- Workflow Config Component ---
const FLOW_COLORS = {
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', header: 'text-blue-800', iconBg: 'bg-blue-100 text-blue-600', stepActive: 'bg-blue-100 border-blue-300 text-blue-800', connector: 'bg-blue-300' },
  emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', header: 'text-emerald-800', iconBg: 'bg-emerald-100 text-emerald-600', stepActive: 'bg-emerald-100 border-emerald-300 text-emerald-800', connector: 'bg-emerald-300' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-200', header: 'text-amber-800', iconBg: 'bg-amber-100 text-amber-600', stepActive: 'bg-amber-100 border-amber-300 text-amber-800', connector: 'bg-amber-300' },
};

export const WorkflowConfig = ({ flowKey, title, icon: Icon, color, config, onToggle, steps }) => {
  const colors = FLOW_COLORS[color];
  const activeSteps = steps.filter(s => s.active);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-xl border ${colors.border} overflow-hidden`}
      data-testid={`workflow-config-${flowKey}`}
    >
      <div className={`px-6 py-4 ${colors.bg} border-b ${colors.border}`}>
        <h3 className={`font-semibold ${colors.header} flex items-center gap-2`}>
          <div className={`w-8 h-8 rounded-lg ${colors.iconBg} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
          {title}
        </h3>
      </div>
      <div className="p-6">
        {/* Visual pipeline */}
        <div className="mb-6">
          <p className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-semibold">Pipeline attiva</p>
          <div className="flex flex-wrap items-center gap-1">
            {activeSteps.map((step, i) => (
              <div key={step.id} className="flex items-center">
                <span className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium border inline-flex items-center gap-1.5",
                  colors.stepActive
                )}>
                  {step.label}
                  {step.email && step.active && <Mail className="w-3 h-3 opacity-60" />}
                </span>
                {i < activeSteps.length - 1 && (
                  <div className={cn("w-5 h-0.5 mx-0.5", colors.connector)} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step toggles */}
        <div className="space-y-3">
          {steps.map(step => (
            <div 
              key={step.id}
              className={cn(
                "flex items-center justify-between p-3 rounded-lg border transition-colors",
                step.active ? "bg-white border-gray-200" : "bg-gray-50 border-gray-100"
              )}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className={cn(
                  "w-2.5 h-2.5 rounded-full flex-shrink-0",
                  step.active ? colors.connector : "bg-gray-300"
                )} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={cn(
                      "text-sm font-medium",
                      step.active ? "text-gray-900" : "text-gray-400"
                    )}>
                      {step.label}
                      {step.fixed && (
                        <span className="ml-2 text-[10px] text-gray-400 font-normal uppercase">obbligatorio</span>
                      )}
                    </p>
                    {step.email && step.active && (
                      <div className="relative group/email">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-200 cursor-default">
                          <Mail className="w-3 h-3" />
                          Email
                        </span>
                        <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover/email:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 max-w-xs">
                          <p className="font-semibold mb-0.5">Template: {step.email.template}</p>
                          <p className="text-gray-300 text-[11px]">{step.email.label}</p>
                          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-gray-900 rotate-45" />
                        </div>
                      </div>
                    )}
                  </div>
                  <p className={cn("text-xs mt-0.5", step.active ? "text-gray-500" : "text-gray-300")}>
                    {step.desc}
                  </p>
                </div>
              </div>
              {!step.fixed && (
                <Switch
                  checked={step.active}
                  onCheckedChange={(checked) => onToggle(step.toggleKey, checked)}
                  data-testid={`workflow-${flowKey}-${step.toggleKey}`}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};
